import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_LOCALE,
  LOCALES,
  MESSAGES,
  MODULE_LABEL_KEYS,
  PAGE_TITLE_KEYS,
  PATH_HINT_KEYS,
  PATH_LABEL_KEYS,
  intlLocaleFor,
} from '../i18n/messages';
import { titleForPath as catalogTitleForPath } from '../components/office/navModules';

export const LANGUAGE_STORAGE_KEY = 'aurax-locale';

const LanguageContext = createContext(null);

function readStoredLocale() {
  // A plataforma e o Web Player usam português, inclusive em perfis antigos.
  return DEFAULT_LOCALE;
}

function applyHtmlLang(code) {
  const meta = LOCALES.find((l) => l.code === code) || LOCALES[0];
  try {
    document.documentElement.lang = meta.htmlLang;
  } catch {
    /* ignore */
  }
}

function formatMessage(template, vars = {}) {
  if (!template) return '';
  return String(template).replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : `{${key}}`,
  );
}

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(readStoredLocale);

  useEffect(() => {
    applyHtmlLang(locale);
    try { window.localStorage.setItem(LANGUAGE_STORAGE_KEY, DEFAULT_LOCALE); } catch { /* private mode */ }
  }, [locale]);

  const setLocale = useCallback(() => {
    const next = DEFAULT_LOCALE;
    setLocaleState(next);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    applyHtmlLang(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = MESSAGES[locale] || MESSAGES[DEFAULT_LOCALE];
      const fallback = MESSAGES[DEFAULT_LOCALE];
      const raw = dict[key] ?? fallback[key] ?? key;
      return vars ? formatMessage(raw, vars) : raw;
    },
    [locale],
  );

  const labelForPath = useCallback(
    (path) => {
      const key = PATH_LABEL_KEYS[path];
      return key ? t(key) : path;
    },
    [t],
  );

  const hintForPath = useCallback(
    (path) => {
      const key = PATH_HINT_KEYS[path];
      return key ? t(key) : '';
    },
    [t],
  );

  const titleForPath = useCallback(
    (pathname) => {
      const path = String(pathname || '')
        .split('?')[0]
        .replace(/\/+$/, '') || '/';

      const exact = PAGE_TITLE_KEYS[path] || PATH_LABEL_KEYS[path];
      if (exact) return t(exact);

      let bestPath = '';
      let bestKey = '';
      const catalog = { ...PATH_LABEL_KEYS, ...PAGE_TITLE_KEYS };
      for (const [p, key] of Object.entries(catalog)) {
        if (!p || p === '/') continue;
        if (path === p || path.startsWith(`${p}/`)) {
          if (p.length >= bestPath.length) {
            bestPath = p;
            bestKey = key;
          }
        }
      }
      if (bestKey) return t(bestKey);
      // Fallback: catálogo do menu (evita «Escritório» em rotas novas sem i18n).
      const catalogTitle = catalogTitleForPath(path);
      if (catalogTitle && catalogTitle !== 'Escritório') return catalogTitle;
      return t('page.office');
    },
    [t],
  );

  const translateModules = useCallback(
    (modules) =>
      (modules || []).map((mod) => {
        const modKey = MODULE_LABEL_KEYS[mod.id];
        return {
          ...mod,
          label: modKey ? t(modKey) : mod.label,
          items: (mod.items || []).map((it) => {
            const pathKey = PATH_LABEL_KEYS[it.path];
            return {
              ...it,
              label: pathKey ? t(pathKey) : it.label,
            };
          }),
        };
      }),
    [t],
  );

  const translateTools = useCallback(
    (tools) =>
      (tools || []).map((tool) => ({
        ...tool,
        label: PATH_LABEL_KEYS[tool.path] ? t(PATH_LABEL_KEYS[tool.path]) : tool.label,
        hint: PATH_HINT_KEYS[tool.path] ? t(PATH_HINT_KEYS[tool.path]) : tool.hint,
      })),
    [t],
  );

  const formatInt = useCallback(
    (v) => Number(v || 0).toLocaleString(intlLocaleFor(locale)),
    [locale],
  );

  const formatBRL = useCallback(
    (v) =>
      new Intl.NumberFormat(intlLocaleFor(locale), {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(v) || 0),
    [locale],
  );

  const value = useMemo(
    () => ({
      locale,
      locales: LOCALES.filter((item) => item.code === DEFAULT_LOCALE),
      setLocale,
      t,
      labelForPath,
      hintForPath,
      titleForPath,
      translateModules,
      translateTools,
      formatInt,
      formatBRL,
      intlLocale: intlLocaleFor(locale),
    }),
    [
      locale,
      setLocale,
      t,
      labelForPath,
      hintForPath,
      titleForPath,
      translateModules,
      translateTools,
      formatInt,
      formatBRL,
    ],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
